'use client'

/**
 * The docked chat's client entry point (Issue #13407).
 *
 * ## Why this file exists
 *
 * Before #13407 `AiChatWrapper` rendered the provider, the FAB and the panel
 * as one unit inside the frame's `sidecar` slot. That placement made the
 * provider a DOM **sibling** of the application pane, so `useAiChat()` called
 * from inside a page resolved to `AI_CHAT_FAIL_OPEN_DEFAULT` and every method
 * on it was a silent no-op — which is why Home's first cut (#13391) had to
 * hand its question over through `ai-chat:open` / `ai-chat:send` window events
 * and let the sidecar answer.
 *
 * Home now holds the conversation in the page, so it has to drive the SAME
 * store the panel drives. Two placements were available and only one of them
 * is legal here:
 *
 *  - Hoist `AiChatWrapper` itself above the frame. Rejected: it is an async
 *    server component doing an org read and a person read, and
 *    `tests/unit/app/root-layout-app-frame-composition-12946.test.ts` exists
 *    specifically to stop `{children}` ending up behind that read. Every
 *    route's first paint would wait on the chat's enablement check, with no
 *    visible symptom in development where those reads are warm.
 *  - Hoist the PROVIDER (a client component that needs no server data to
 *    mount) above the frame, and keep the server gate exactly where it is.
 *    This file is the seam that makes the second one work: it runs inside the
 *    gated tree and latches the provider on.
 *
 * So the session check still happens in the sidecar's `<Suspense>`, still
 * streams, and still costs a signed-out request nothing — `activateChat` is
 * never called for it, and the provider fetches no conversations until it
 * is. (AI chat is core since #16724: no org setting gates it.)
 *
 * ## It renders the chrome too
 *
 * The FAB and the panel stay here rather than moving up with the provider,
 * because they are the DOCKED surface and the docked surface belongs in the
 * sidecar cell. Both suppress themselves on Home through `shouldHideAiChat`.
 */

import { useEffect, useRef } from 'react'

import { AiChatFab } from './ai-chat-fab'
import { AiChatPanel } from './ai-chat-panel'
import { useAiChat, type AiChatUserInfo } from './ai-chat-provider'
import { useRegisterAskBarAttachments } from './use-ask-bar-attachments'

export function AiChatRuntime({
  orgId,
  userInfo,
}: {
  /**
   * The org whose gate said yes (#13407 review).
   *
   * Passed down so the provider can tell a re-activation from a NEW session.
   * The provider outlives any one org now — it is above the frame, and org
   * switch and impersonation are soft refreshes — so without this it would
   * carry the previous org's conversation list into the next one.
   */
  orgId: string
  userInfo?: AiChatUserInfo
}) {
  const { activateChat, deactivateChat } = useAiChat()
  // #16102 — the hand-off ask bars take files while this panel, which
  // receives them, is mounted. Files are part of AI chat (#16724).
  useRegisterAskBarAttachments()

  // Field-wise dependencies, not the object: `userInfo` is deserialized fresh
  // from the server payload on every render of the wrapper above, so an
  // identity dependency would re-run this effect forever. `activateChat`
  // bails out on an equal org and user anyway; this keeps the effect quiet.
  useEffect(() => {
    activateChat(
      orgId,
      userInfo?.id
        ? { id: userInfo.id, name: userInfo.name, email: userInfo.email }
        : undefined,
    )
  }, [activateChat, orgId, userInfo?.id, userInfo?.name, userInfo?.email])

  // Teardown is the other half of the gate, and it is its OWN effect
  // (#13407 review). This component renders only while `AiChatWrapper`'s org
  // check passes, so its unmount IS the gate closing — and since the hoist
  // that unmount no longer takes the provider with it. Deactivating is what
  // stops a chat-disabled org inheriting the previous org's store and a
  // latched-on `isChatEnabled`.
  //
  // Kept separate from the activation effect rather than returned as its
  // cleanup, because React runs a cleanup before every RE-RUN as well as on
  // unmount: sharing one effect would discard the whole store the moment the
  // viewer's display name changed, then immediately re-activate. Reached
  // through a ref so this effect can hold an empty dependency list and mean
  // it — `deactivateChat` is stable today, and a future edit that makes it
  // otherwise must not silently turn this back into per-render teardown.
  const deactivateRef = useRef(deactivateChat)
  deactivateRef.current = deactivateChat
  useEffect(() => () => deactivateRef.current(), [])

  return (
    <>
      <AiChatFab />
      <AiChatPanel />
    </>
  )
}
